import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Boxes, ExternalLink, FileText, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge, LangBadge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { EmptyState, Separator, Skeleton } from '@/components/ui/misc'
import { useConcepts } from '@/hooks/use-term-data'
import { getConcept } from '@/lib/api/concepts/get-concepts'
import { getMentions } from '@/lib/api/terms/get-mentions'
import { documentFileUrl, getDocumentContext } from '@/lib/api/documents/get-documents'
import {
  cn,
  compareStrings,
  formatNumber,
  highlightMatch,
  highlightSegments,
  sourceLink,
} from '@/lib/utils'
import { LANGUAGES, type Concept, type ConceptDefinition, type ConceptTerm } from '@/types/terms'
import type { ConceptSort } from './concept-filters-panel'

// Rows size themselves to their member count, so this is only the starting
// estimate the virtualiser uses before it has measured anything.
const ROW_ESTIMATE = 78
// Beyond this a single concept would dominate the list; the rest are one click
// away on the card.
const MAX_VISIBLE_MEMBERS = 8

interface ConceptsViewProps {
  collectionId: number | null
  /** Clears the concept selection; the card returns to its empty state. */
  onClose?: () => void
  query: string
  lang: string
  /** Language the concept must NOT contain, for finding gaps. */
  missingLang?: string
  domain?: string
  /** Only concepts with a member among these term ids (the document filter). */
  termIds?: Set<number> | null
  sort?: ConceptSort
  selectedId: number | null
  onSelect: (id: number) => void
  onOpenTerm: (lemma: string, lang: string) => void
  /** Open a document's text view at a page — the citation under each term. */
  onOpenDocument?: (documentId: number, page: number) => void
  /** How many concepts survived the filters, for the panel's shown/total. */
  onShownChange?: (n: number) => void
}

export function ConceptsView({
  collectionId,
  onClose,
  query,
  lang,
  missingLang = '',
  domain = '',
  termIds = null,
  sort = 'terms',
  selectedId,
  onSelect,
  onOpenTerm,
  onOpenDocument,
  onShownChange,
}: ConceptsViewProps) {
  const { t } = useTranslation()
  const { data: concepts, isLoading } = useConcepts(collectionId)
  const parentRef = useRef<HTMLDivElement>(null)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const list = (concepts ?? []).filter((c) => {
      if (lang && !c.terms.some((t) => t.lang === lang)) return false
      // The gap finder: concepts still waiting for an equivalent in this
      // language. Inverted on purpose — it is the one question a trilingual
      // term base is actually asking of its own contents.
      if (missingLang && c.terms.some((t) => t.lang === missingLang)) return false
      if (termIds && !c.terms.some((t) => t.term_id != null && termIds.has(t.term_id))) {
        return false
      }
      if (domain && !(c.domains ?? '').split(',').map((d) => d.trim()).includes(domain)) {
        return false
      }
      if (!q) return true
      return c.terms.some((t) => t.value.toLowerCase().includes(q))
    })

    const langsOf = (c: Concept) => new Set(c.terms.map((x) => x.lang)).size
    const firstOf = (c: Concept) => c.terms[0]?.value ?? ''
    const cmp: Record<ConceptSort, (a: Concept, b: Concept) => number> = {
      terms: (a, b) => b.terms.length - a.terms.length,
      // Fewest languages first: the concepts closest to being complete, and
      // the ones worth a curator's attention, sit at the top.
      gaps: (a, b) => langsOf(a) - langsOf(b) || b.terms.length - a.terms.length,
      alpha: (a, b) => compareStrings(firstOf(a), firstOf(b)),
    }
    return [...list].sort(cmp[sort])
  }, [concepts, query, lang, missingLang, domain, termIds, sort])

  useEffect(() => {
    onShownChange?.(filtered.length)
  }, [filtered.length, onShownChange])

  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_ESTIMATE,
    // Measured heights are cached per item. Without a stable key the cache is
    // keyed by index, so filtering or reordering re-uses one concept's measured
    // height for whichever concept now sits at that index — which is what made
    // rows overlap and leave gaps.
    getItemKey: (index) => filtered[index]?.concept_id ?? index,
    overscan: 10,
  })

  return (
    <>
      <div className="flex min-h-0 w-[420px] shrink-0 flex-col border-r border-border">
        <div className="border-b border-border px-3 py-1.5 text-[11px] text-muted-foreground">
          {t('units.concepts', { count: filtered.length })}
        </div>
        {isLoading ? (
          <div className="flex flex-col gap-1 p-2">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : !filtered.length ? (
          <EmptyState title={t('concepts.empty')} />
        ) : (
          <div ref={parentRef} className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
            <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
              {virtualizer.getVirtualItems().map((row) => {
                const c = filtered[row.index]
                const isSelected = c.concept_id === selectedId
                return (
                  <button
                    // Must match getItemKey, so React's DOM reuse and the
                    // virtualiser's measurement cache agree on identity.
                    key={row.key}
                    // Measured rather than fixed: a concept's height depends on
                    // how many members it has.
                    ref={virtualizer.measureElement}
                    data-index={row.index}
                    type="button"
                    onClick={() => onSelect(c.concept_id)}
                    className={cn(
                      'absolute left-0 top-0 flex w-full flex-col gap-1 border-b border-border/60 px-3 py-2 text-left transition-colors',
                      isSelected ? 'bg-accent' : 'hover:bg-muted',
                    )}
                    style={{ transform: `translateY(${row.start}px)` }}
                  >
                    {/* One member per line: a concept is a set of equivalent
                        expressions, and stacking them lets the eye compare the
                        languages down a column instead of reading a truncated
                        run-on line. */}
                    <div className="flex w-full min-w-0 flex-col gap-0.5">
                      {c.terms.slice(0, MAX_VISIBLE_MEMBERS).map((ct, i) => (
                        <span
                          key={i}
                          className="flex min-w-0 items-center gap-1.5 text-[12px] leading-tight"
                        >
                          <span
                            aria-hidden
                            className="shrink-0 text-muted-foreground/60"
                          >
                            •
                          </span>
                          {/* Term first, language after: the term is what the
                              eye is scanning for, so it starts at a common left
                              edge instead of being pushed right by the badge. */}
                          <span className={cn('truncate', ct.is_preferred && 'font-semibold')}>
                            {highlightMatch(ct.value, query).map((seg, j) =>
                              seg.match ? (
                                <mark
                                  key={j}
                                  className="rounded-sm bg-highlight px-0.5 text-highlight-foreground"
                                >
                                  {seg.text}
                                </mark>
                              ) : (
                                <span key={j}>{seg.text}</span>
                              ),
                            )}
                          </span>
                          <LangBadge lang={ct.lang} className="h-3.5 min-w-5 shrink-0 text-[9px]" />
                        </span>
                      ))}
                      {c.terms.length > MAX_VISIBLE_MEMBERS && (
                        <span className="text-[11px] text-muted-foreground">
                          {t('actions.andMore', {
                            count: c.terms.length - MAX_VISIBLE_MEMBERS,
                          })}
                        </span>
                      )}
                    </div>
                    <div className="truncate text-[10px] text-muted-foreground">
                      #{c.concept_id} · {c.domains || '—'}
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <ConceptCard
          conceptId={selectedId}
          onOpenTerm={onOpenTerm}
          onOpenDocument={onOpenDocument}
          onClose={onClose}
        />
      </div>
    </>
  )
}

function ConceptCard({
  conceptId,
  onOpenTerm,
  onOpenDocument,
  onClose,
}: {
  conceptId: number | null
  onOpenTerm: (lemma: string, lang: string) => void
  onOpenDocument?: (documentId: number, page: number) => void
  onClose?: () => void
}) {
  const { t } = useTranslation()
  const { data: concept, isLoading } = useQuery({
    queryKey: ['concept', conceptId],
    queryFn: () => getConcept(conceptId!),
    enabled: !!conceptId,
  })

  if (!conceptId) {
    return (
      <EmptyState icon={Boxes} title={t('concepts.select')} hint={t('concepts.selectHint')} />
    )
  }
  if (isLoading || !concept) {
    return (
      <div className="flex flex-col gap-2 p-4">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  const byLang = groupByLang(concept)
  const quotes = (concept.definitions ?? []).filter((d) => d.type.startsWith('seletus'))
  const drafts = (concept.definitions ?? []).filter((d) => d.type === 'autogen')

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-border bg-accent/40 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold">
            {t('concepts.heading', { id: concept.concept_id })}
          </h2>
          {onClose && (
            <Button
              variant="ghost"
              size="icon-sm"
              className="ml-auto"
              onClick={onClose}
              title={t('common.close')}
              aria-label={t('common.close')}
            >
              <X />
            </Button>
          )}
        </div>
        {concept.domains && (
          <p className="mt-1 text-xs text-muted-foreground">{concept.domains}</p>
        )}
      </div>

      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto p-4">

      {(quotes.length > 0 || drafts.length > 0) && (
        <ConceptDefinitions
          quotes={quotes}
          drafts={drafts}
          terms={concept.terms}
          onOpenDocument={onOpenDocument}
        />
      )}

      {LANGUAGES.filter((l) => byLang[l]?.length).map((l) => (
        <div key={l} className="mt-4">
          <div className="mb-1.5 flex items-center gap-2">
            <LangBadge lang={l} />
            <Separator className="flex-1" />
          </div>
          <div className="flex flex-col gap-1.5">
            {byLang[l].map((ct, i) => (
              <div key={i} className="rounded-md border border-border px-2.5 py-1.5">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0 text-sm font-semibold"
                    onClick={() => onOpenTerm(ct.value, ct.lang)}
                  >
                    {ct.value}
                  </Button>
                  {ct.term_type === 'abbreviation' && (
                    <Badge variant="outline">{t('terms.abbreviation')}</Badge>
                  )}
                  {typeof ct.freq === 'number' && (
                    <span className="text-[11px] tabular-nums text-muted-foreground">
                      {t('concepts.freqInDocs', {
                        freq: formatNumber(ct.freq),
                        docs: formatNumber(ct.docs),
                      })}
                    </span>
                  )}
                </div>
                {ct.context && (
                  <TermCitation term={ct} onOpenDocument={onOpenDocument} onOpenTerm={onOpenTerm} />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      </div>
    </div>
  )
}

const QUOTES_COLLAPSED = 3

/**
 * The two kinds of definition material a concept can carry, and nothing else:
 * sentences quoted verbatim from documents (with where they were found), and
 * machine drafts marked as such. This is a discovery tool — there is no
 * approving or editing here; the draft exists to be copied into the
 * terminologist's own tool.
 */
function ConceptDefinitions({
  quotes,
  drafts,
  terms,
  onOpenDocument,
}: {
  quotes: ConceptDefinition[]
  drafts: ConceptDefinition[]
  /** The concept's terms — a quote's PDF link highlights the one in its language. */
  terms: ConceptTerm[]
  onOpenDocument?: (documentId: number, page: number) => void
}) {
  const termFor = (lang: string) =>
    (terms.find((x) => x.lang === lang && x.is_preferred) ?? terms.find((x) => x.lang === lang))
      ?.value
  const { t, i18n } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState<number | null>(null)
  // Quotes are grouped by language, the same way the concept's terms are.
  // Ordering first and slicing after keeps the collapsed view honest: it shows
  // the first few in language order rather than a few from each group.
  const ordered = useMemo(() => {
    const rank = (l: string) => {
      const i = LANGUAGES.indexOf(l as (typeof LANGUAGES)[number])
      return i === -1 ? LANGUAGES.length : i
    }
    return [...quotes].sort((a, b) => rank(a.lang) - rank(b.lang))
  }, [quotes])
  const shown = expanded ? ordered : ordered.slice(0, QUOTES_COLLAPSED)
  const hidden = ordered.length - shown.length
  // `shown` is sorted by language, so consecutive runs are the groups.
  const groups = useMemo(() => {
    const out: { lang: string; items: ConceptDefinition[] }[] = []
    for (const q of shown) {
      const last = out[out.length - 1]
      if (last && last.lang === q.lang) last.items.push(q)
      else out.push({ lang: q.lang, items: [q] })
    }
    return out
  }, [shown])

  const copy = (i: number, text: string) => {
    const done = () => {
      setCopied(i)
      window.setTimeout(() => setCopied((c) => (c === i ? null : c)), 1400)
    }
    try {
      navigator.clipboard.writeText(text).then(done, done)
    } catch {
      done()
    }
  }

  const fmtDate = (iso?: string | null) => {
    if (!iso) return ''
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(i18n.language)
  }

  return (
    <div className="flex flex-col gap-4 pb-1">
      <section>
        <div className="mb-1.5 flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
          <span>{t('concepts.quotes')}</span>
          <span className="tabular-nums font-semibold text-foreground">{quotes.length}</span>
        </div>
        {quotes.length === 0 ? (
          <p className="text-[12px] text-muted-foreground">{t('concepts.quotesNone')}</p>
        ) : (
          <div className="flex flex-col gap-3">
            {groups.map((g) => (
              <div key={g.lang} className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                  <LangBadge lang={g.lang} />
                  <Separator className="flex-1" />
                </div>
                {g.items.map((d, i) => {
                  const docId = d.document_id ?? null
                  const page = d.page ?? 0
                  return (
                    <blockquote
                      key={i}
                      className="m-0 rounded-r-md border-l-2 border-primary bg-muted/60 px-3 py-2"
                    >
                      <p className="text-[13px] leading-snug">{d.definition}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-muted-foreground">
                        {onOpenDocument && docId ? (
                          <button
                            type="button"
                            onClick={() => onOpenDocument(docId, page)}
                            className="truncate text-left font-medium hover:text-primary hover:underline"
                            title={t('mentions.openDocument')}
                          >
                            {d.source}
                          </button>
                        ) : (
                          d.source && <span className="truncate font-medium">{d.source}</span>
                        )}
                        {page > 0 && <span className="shrink-0">{t('common.page', { page })}</span>}
                        {d.has_file && docId && (
                          <a
                            href={documentFileUrl(docId, page, termFor(d.lang))}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="shrink-0 text-primary hover:underline"
                            title={t('mentions.openPdf')}
                          >
                            <FileText className="size-3" />
                          </a>
                        )}
                        {d.url && (
                          <a
                            href={sourceLink(d.url) ?? undefined}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="shrink-0 text-primary hover:underline"
                            title={t('mentions.openSource')}
                          >
                            <ExternalLink className="size-3" />
                          </a>
                        )}
                      </div>
                      <QuoteContext
                        documentId={docId}
                        document={d.source}
                        page={page}
                        quote={d.definition}
                        url={d.url}
                        hasFile={d.has_file}
                        onOpenDocument={onOpenDocument}
                      />
                    </blockquote>
                  )
                })}
              </div>
            ))}
            {(hidden > 0 || expanded) && ordered.length > QUOTES_COLLAPSED && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="self-start text-[12px] text-primary hover:underline"
              >
                {expanded ? t('concepts.showLess') : t('concepts.showMore', { count: hidden })}
              </button>
            )}
          </div>
        )}
      </section>

      {drafts.length > 0 && (
        <section>
          <div className="mb-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
            {t('concepts.draft')}
          </div>
          <div className="flex flex-col gap-1.5">
            {drafts.map((d, i) => {
              const meta = [
                d.lang.toUpperCase(),
                d.based_on === 'quotes'
                  ? t('concepts.draftFromQuotes')
                  : d.based_on === 'contexts'
                    ? t('concepts.draftFromContexts')
                    : null,
                d.model,
                fmtDate(d.date_created),
              ].filter(Boolean)
              return (
                <div
                  key={i}
                  className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 rounded-md border border-dashed border-warning/50 bg-warning/5 px-3 py-2"
                >
                  <p className="text-[13px] leading-snug">{d.definition}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="row-span-2 h-6 self-start px-2 text-[11px]"
                    onClick={() => copy(i, d.definition)}
                  >
                    {copied === i ? t('concepts.copied') : t('concepts.copy')}
                  </Button>
                  <span className="text-[10px] tracking-wide text-muted-foreground">
                    {meta.join(' · ')}
                  </span>
                </div>
              )
            })}
          </div>
        </section>
      )}
    </div>
  )
}

/**
 * One citation under a concept term, laid out like a row of the term's
 * mention list: document (opens the text view at the page), page, PDF and
 * source links, then the sentence with the surface form lit up.
 */
/**
 * "Show context": the paragraph around a quoted sentence, fetched on demand
 * from the document's stored text, with the quote lit up inside it. Quotes
 * are cut to one entry on purpose; this is the way back to what surrounded
 * them without leaving the card.
 */
function QuoteContext({
  documentId,
  document,
  page,
  quote,
  url,
  hasFile,
  onOpenDocument,
}: {
  documentId: number | null
  document: string
  page: number
  quote: string
  url?: string | null
  hasFile?: boolean
  onOpenDocument?: (documentId: number, page: number) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const { data, isLoading } = useQuery({
    queryKey: ['doc-context', documentId, page, quote],
    queryFn: () => getDocumentContext(documentId!, quote, page),
    enabled: open && documentId != null && quote.length >= 10,
    staleTime: 10 * 60 * 1000,
  })
  if (documentId == null || quote.length < 10) return null
  // The passage may sit on a different page than the quote row claims
  // (older extractions), so the links here use the page it was found on.
  const foundPage = data?.text ? data.page : page
  const href = sourceLink(url, quote.slice(0, 80))
  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[11px] text-primary hover:underline"
      >
        {open ? t('concepts.hideContext') : t('concepts.showContext')}
      </button>
      {open && (
        <div className="mt-1 rounded-md bg-background/70 px-2.5 py-2 text-[12px] leading-snug text-muted-foreground">
          <div className="mb-1 flex items-center gap-1.5 text-[11px]">
            {onOpenDocument ? (
              <button
                type="button"
                onClick={() => onOpenDocument(documentId, foundPage)}
                className="truncate text-left font-medium hover:text-primary hover:underline"
                title={t('mentions.openDocument')}
              >
                {document}
              </button>
            ) : (
              <span className="truncate font-medium">{document}</span>
            )}
            {foundPage > 0 && (
              <span className="shrink-0">{t('common.page', { page: foundPage })}</span>
            )}
            {hasFile && (
              <a
                href={documentFileUrl(documentId, foundPage)}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 text-primary hover:underline"
                title={t('mentions.openPdf')}
              >
                <FileText className="size-3" />
              </a>
            )}
            {href && (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 text-primary hover:underline"
                title={t('mentions.openSource')}
              >
                <ExternalLink className="size-3" />
              </a>
            )}
          </div>
          {isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : !data || !data.text ? (
            <span>{t('concepts.contextNotFound')}</span>
          ) : (
            <p className="m-0">
              {data.truncated_start && '… '}
              {data.text.slice(0, data.start)}
              <mark className="rounded-sm bg-highlight px-0.5 text-highlight-foreground">
                {data.text.slice(data.start, data.end)}
              </mark>
              {data.text.slice(data.end)}
              {data.truncated_end && ' …'}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

/** Occurrences revealed per click under a concept term. */
const OCCURRENCES_STEP = 5

/**
 * One occurrence line, shared by the concept term's citation and the extra
 * occurrences it expands to: document (opens the text view at the page),
 * page, PDF and source links, then the sentence with the surface lit up.
 */
function CitationRow({
  sentence,
  surface,
  document,
  page,
  documentId,
  url,
  hasFile,
  onOpenDocument,
}: {
  sentence: string
  surface: string
  document: string
  page: number
  documentId: number | null
  url?: string | null
  hasFile?: boolean
  onOpenDocument?: (documentId: number, page: number) => void
}) {
  const { t } = useTranslation()
  const href = sourceLink(url, surface)
  return (
    <div className="border-l-2 border-border pl-2">
      <div className="mb-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
        {onOpenDocument && documentId ? (
          <button
            type="button"
            onClick={() => onOpenDocument(documentId, page)}
            className="truncate text-left font-medium hover:text-primary hover:underline"
            title={t('mentions.openDocument')}
          >
            {document}
          </button>
        ) : (
          <span className="truncate font-medium">{document}</span>
        )}
        {page > 0 && <span className="shrink-0">{t('common.page', { page })}</span>}
        {hasFile && documentId && (
          <a
            href={documentFileUrl(documentId, page, surface)}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 text-primary hover:underline"
            title={t('mentions.openPdf')}
          >
            <FileText className="size-3" />
          </a>
        )}
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 text-primary hover:underline"
            title={t('mentions.openSource')}
          >
            <ExternalLink className="size-3" />
          </a>
        )}
      </div>
      <p className="text-[12px] leading-snug text-muted-foreground">
        {highlightSegments(sentence, surface).map((seg, i) =>
          seg.match ? (
            <mark
              key={i}
              className="rounded-sm bg-highlight px-0.5 font-medium text-highlight-foreground"
            >
              {seg.text}
            </mark>
          ) : (
            <span key={i}>{seg.text}</span>
          ),
        )}
      </p>
      <QuoteContext
        documentId={documentId}
        document={document}
        page={page}
        quote={sentence}
        url={url}
        hasFile={hasFile}
        onOpenDocument={onOpenDocument}
      />
    </div>
  )
}

const normSentence = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim()

/**
 * The citation under a concept term, plus its other occurrences on demand.
 * One sentence is enough to place a term; the rest load only when asked, in
 * steps, because the busiest terms have thousands and the concept card is
 * not the place to scroll them — the term page is, one click further.
 */
function TermCitation({
  term: ct,
  onOpenDocument,
  onOpenTerm,
}: {
  term: Concept['terms'][number]
  onOpenDocument?: (documentId: number, page: number) => void
  onOpenTerm: (lemma: string, lang: string) => void
}) {
  const { t } = useTranslation()
  const [shown, setShown] = useState(0)
  const termId = ct.term_id ?? null
  const { data: mentions, isLoading } = useQuery({
    queryKey: ['mentions', termId, 'terms'],
    queryFn: () => getMentions(termId!, 'terms'),
    enabled: shown > 0 && termId != null,
    staleTime: 5 * 60 * 1000,
  })
  // The citation itself is one of the mentions; do not list it twice.
  const others = useMemo(() => {
    const cited = normSentence(ct.context ?? '')
    return (mentions ?? []).filter((m) => normSentence(m.sentence) !== cited)
  }, [mentions, ct.context])
  const total = typeof ct.freq === 'number' ? Math.max(ct.freq - 1, 0) : null
  const remaining = shown > 0 && mentions ? others.length - shown : (total ?? 0)

  return (
    <div className="mt-1.5 flex flex-col gap-1.5">
      <CitationRow
        sentence={ct.context ?? ''}
        surface={ct.context_surface || ct.value}
        document={ct.context_source ?? ''}
        page={ct.context_page ?? 0}
        documentId={ct.context_document_id ?? null}
        url={ct.context_url}
        hasFile={ct.context_has_file}
        onOpenDocument={onOpenDocument}
      />
      {shown > 0 &&
        others.slice(0, shown).map((m, i) => (
          <CitationRow
            key={i}
            sentence={m.sentence}
            surface={m.surface || ct.value}
            document={m.document}
            page={m.page}
            documentId={m.document_id ?? null}
            url={m.url}
            hasFile={m.has_file}
            onOpenDocument={onOpenDocument}
          />
        ))}
      {termId != null && (total === null || total > 0) && (
        <div className="flex flex-wrap items-center gap-x-3 text-[11px]">
          {isLoading ? (
            <span className="text-muted-foreground">{t('concepts.loadingOccurrences')}</span>
          ) : remaining > 0 ? (
            <button
              type="button"
              onClick={() => setShown((n) => n + OCCURRENCES_STEP)}
              className="text-primary hover:underline"
            >
              {t('concepts.moreOccurrences', { count: remaining })}
            </button>
          ) : null}
          {shown > 0 && !isLoading && (
            <>
              <button
                type="button"
                onClick={() => setShown(0)}
                className="text-muted-foreground hover:underline"
              >
                {t('concepts.hideOccurrences')}
              </button>
              <button
                type="button"
                onClick={() => onOpenTerm(ct.value, ct.lang)}
                className="text-muted-foreground hover:underline"
              >
                {t('concepts.allOccurrences')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function groupByLang(concept: Concept) {
  const out: Record<string, Concept['terms']> = {}
  for (const t of concept.terms) (out[t.lang] ??= []).push(t)
  return out
}
